import {
  calculateExpression
} from "../expression/expressionFunctions";
import { templateExpressionToExpression } from "../expression/expressionTemplates";

import type { ExpressionValue } from "../expression/expressionFunctions";
import type { ExpressionParseContext } from "../effect/Expression";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "./IndexItem";
import type { NetItems } from "@editor/net/netCanvasInjectionKeys";

export function calculateExpressionValues(
  items: {
    [id: number]: {
      valueExpression: string;
      unit?: string | null;
      type: "target" | "resource" | "index" | "flow";
    };
  },
  context: ExpressionParseContext
) {
  const itemValues: { [uid: string]: ExpressionValue } = {};

  for (const [_itemId, item] of Object.entries(items)) {
    const itemId = parseInt(_itemId, 10);

    // don't recalculate if already calculated
    if (itemId in itemValues && itemValues[itemId] != null) continue;

    const type = item.type;

    itemValues[`${type}_${itemId}`] = getExpressionValue(item.valueExpression, {
      ...context,
      indexValueCache: itemValues,
      affectedId: itemId,
      affectedType: type
    });

    // ensure the correct unit is set
    if (item.unit) {
      itemValues[`${type}_${itemId}`].unit = item.unit;
      itemValues[`${type}_${itemId}`].unitValid = true;
    }
  }

  return itemValues;
}

/**
 * Calculate the value of an expression.
 * Don't use this function directly, but use `calculateExpressionValues` instead.
 * That way, the indices that other are dependent on will be calculated first.
 */
function getExpressionValue(
  valueExpression: string,
  context: ExpressionParseContext
): ExpressionValue {
  try {
    return calculateExpression(valueExpression, context);
  } catch (error) {
    console.log("error in expression:", valueExpression);
    console.error(error);
    return { value: 0, unitValid: false, unit: "", minValue: 0, maxValue: 0, valid: false };
  }
}

export function getAllReferencedItems(
  expression: string,
  netItems: NetItems
): (IndexItem | TargetItem | ResourceItem | undefined)[] {
  const targets = netItems.target;
  const indices = netItems.index;
  const resources = netItems.resource;

  const resolvedExpression = templateExpressionToExpression(expression, {
    targets,
    indices,
    resources
  });

  const items = [
    ...resolvedExpression.matchAll(/(resource|target|index)_(\d+)/g)
  ].map((match) => {
    const type = match[1] as "resource" | "target" | "index";
    const id = parseInt(match[2], 10);

    if (type === "target") {
      return targets?.[id];
    } else if (type === "resource") {
      return resources?.[id];
    } else if (type === "index") {
      return indices?.[id];
    }

    throw new Error("Invalid type");
  });

  return items;
}
